import React from "react";
import { Helmet } from "react-helmet-async";
import useLocale, { I18N, LocaleString } from "../../util/i18n";
import { getConfig, saveConfig } from "../../api/core";
import { MDBTable, MDBTableBody, MDBTableHead } from "mdb-react-ui-kit";
import { CancelablePromise } from "../../util/client";
import { useCrumbs } from "../../component/breadcrumbs";
import { faPlus, faSave, faTrash } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCalendarXmark } from "@fortawesome/free-regular-svg-icons";
import Toast from "../../component/toast";
import env from "../../env.json";
import { intersection } from "lodash";
import { getParsingRules, saveParsingRules, type CodeFormatRow } from "../../api/inventory";

const EXCEPTIONS = { 'inventory/pad': 'Pad Label', 'inventory/skip': 'No Label', 'inventory/combine': 'Combined Labels', 'inventory/confirm': 'Confirm Needed', 'inventory/separate': 'Separated Labels' };
const init: DataType = { 'inventory/pad': [], 'inventory/skip': [], 'inventory/combine': [], 'inventory/confirm': [], 'inventory/separate': [] };
type ConfigRow = {
    sku: Array<string>;
    match?: boolean;
    divide?: number | Array<number>;
    display?: string | Array<string> | LocaleString;
} | Array<string>;
type DataType = Record<keyof typeof EXCEPTIONS, Array<ConfigRow>>;

export default function ConfigInventory() {
    const { 0: data, 1: setData } = React.useState<DataType>({ ...init });
    const { 0: format, 1: setFormat } = React.useState<ParseRules>(env.defaultParseRules);
    const { 0: __, 1: lang } = useLocale();
    const { 0: setCrumbs } = useCrumbs();
    const debounce = React.useRef<number | null>(null);
    const last = React.useRef<CancelablePromise<any> | null>(null);
    const saving = React.useCallback((data: DataType) => {
        if (debounce.current) {
            window.clearTimeout(debounce.current);
        }
        let f = () => {
            debounce.current = null;
            last.current = null;
        };
        debounce.current = window.setTimeout(() => {
            if (last.current) {
                last.current.cancel();
            }
            let params = [];
            for (let path in data) {
                params.push({
                    path, value: JSON.stringify(data[path as keyof typeof EXCEPTIONS])
                });
            }
            (last.current = saveConfig(params)).then(f, f);
        }, 1000);
    }, []);
    let doSave = React.useCallback((type: keyof typeof EXCEPTIONS, row: number, key: 'sku' | 'divide' | 'display' | 'match', e: boolean | React.FocusEvent<HTMLTableCellElement>) => {
        setData(data => {
            const cloned = { ...data };
            if (Array.isArray(cloned[type][row])) {
                cloned[type][row] = {
                    sku: cloned[type][row]
                };
            }
            let value: any = typeof e === 'boolean' ? e : e.target.innerText;
            if (key === 'divide') {
                let parts = [];
                for (let part of value.split(',')) {
                    parts.push(parseFloat(part));
                }
                value = parts;
            } else if (type === 'inventory/confirm' && key === 'display') {
                if (lang !== 'en') {
                    if (typeof cloned[type][row][key] === 'string') {
                        value = {
                            default: cloned[type][row][key],
                            [lang]: value
                        };
                    } else {
                        value = { ...cloned[type][row][key], [lang]: value };
                    }
                } else {
                    if (typeof cloned[type][row][key] !== 'string') {
                        value = { ...cloned[type][row][key], default: value };
                    }
                }
            } else if (key === (type === 'inventory/separate' ? 'display' : 'sku')) {
                value = value.split(/\s*[\,\uff0c]\s*/);
            }
            if (value === cloned[type][row][key]) {
                return data;
            }
            //@ts-ignore
            cloned[type][row][key] = value;
            saving(cloned);
            return cloned;
        });
    }, [setData, lang]);
    let doAdd = React.useCallback((type: keyof typeof EXCEPTIONS, e: React.MouseEvent<HTMLButtonElement>) => {
        e.stopPropagation();
        e.preventDefault();
        setData(data => {
            const cloned = { ...data };
            cloned[type].push([]);
            return cloned;
        });
    }, [setData]);
    let doDel = React.useCallback((type: keyof typeof EXCEPTIONS, row: number, e: React.MouseEvent<HTMLButtonElement>) => {
        e.stopPropagation();
        e.preventDefault();
        setData(data => {
            const cloned = { ...data };
            cloned[type].splice(row, 1);
            saving(cloned);
            return cloned;
        });
    }, [setData]);
    React.useEffect(() => {
        getConfig('inventory/%').then(response => {
            let configs: DataType = { ...init };
            for (let i in response) {
                if (i in EXCEPTIONS) {
                    configs[i as keyof typeof EXCEPTIONS] = JSON.parse(response[i]);
                } else if (i === 'inventory/code_format') {
                    setFormat(old => ({ ...JSON.parse(response[i]), rules: old.rules }));
                }
            }
            setData(configs);
        });
    }, []);
    React.useEffect(() => {
        setCrumbs(['Inventory Settings']);
    }, []);
    return (
        <>
            <Helmet>
                <title>{__('Inventory Settings')}</title>
            </Helmet>
            <CodeFormat data={format} setData={setFormat} />
            {Object.keys(data).map(e => {
                return (
                    <MDBTable key={e} striped hover className="inventory-settings caption-top">
                        <caption>
                            <span>{__(EXCEPTIONS[e as keyof typeof EXCEPTIONS])}</span>
                            <button type="button" onClick={doAdd.bind(null, e as keyof typeof EXCEPTIONS)}><FontAwesomeIcon icon={faPlus} /></button>
                        </caption>
                        <MDBTableHead>
                            <tr>
                                <th>SKU</th>
                                <th className="w-1 text-center text-nowrap">{__('Match')}</th>
                                {e === 'inventory/skip' ? null : (
                                    <th className="w-1 text-center text-nowrap">
                                        {__(e === 'inventory/confirm' || e === 'inventory/separate' ? 'Times' : (e === 'inventory/pad' ? 'Cut' : 'Divide'))}
                                    </th>
                                )}
                                <th className={(e === 'inventory/confirm' || e === 'inventory/separate' ? '' : 'w-1 ') + 'text-center text-nowrap'}>
                                    {__(e === 'inventory/pad' ? 'Replace' : 'Display')}
                                </th>
                                <th className="w-1 text-center text-nowrap">{__('Delete')}</th>
                            </tr>
                        </MDBTableHead>
                        <MDBTableBody>
                            {data[e as keyof typeof EXCEPTIONS].length > 0 ? (
                                data[e as keyof typeof EXCEPTIONS].map((item, i) => {
                                    let data = Array.isArray(item) ? { sku: item } : item;
                                    return (
                                        <tr key={e + '-' + i}>
                                            <td contentEditable="plaintext-only" suppressContentEditableWarning onBlur={doSave.bind(null, e as keyof typeof EXCEPTIONS, i, 'sku')}>
                                                {Array.isArray(data.sku) ? data.sku.join(',') : (data.sku || '')}
                                            </td>
                                            <th onClick={doSave.bind(null, e as keyof typeof EXCEPTIONS, i, 'match', !data.match)} className="w-1 text-center">{__(data.match ? 'Match' : 'Contains')}</th>
                                            {e === 'inventory/skip' ? null : (
                                                <td contentEditable="plaintext-only" suppressContentEditableWarning onBlur={doSave.bind(null, e as keyof typeof EXCEPTIONS, i, 'divide')} className="w-1 text-center">{typeof data.divide === 'object' ? data.divide.join(',') : data.divide || 1}</td>
                                            )}
                                            <td contentEditable="plaintext-only" suppressContentEditableWarning onBlur={doSave.bind(null, e as keyof typeof EXCEPTIONS, i, 'display')} className={e === 'inventory/confirm' || e === 'inventory/separate' ? '' : 'w-1'}>
                                                {Array.isArray(data.display) ? data.display.join(',') : (typeof data.display === 'object' ? (data.display[lang] || data.display.default) : (data.display || ''))}
                                            </td>
                                            <td><button type="button" onClick={doDel.bind(null, e as keyof typeof EXCEPTIONS, i)}><FontAwesomeIcon icon={faTrash} /></button></td>
                                        </tr>
                                    )
                                })
                            ) : (
                                <tr>
                                    <td colSpan={e === 'inventory/skip' ? 4 : 5} className="text-center">
                                        <FontAwesomeIcon icon={faCalendarXmark} size="3x" />
                                    </td>
                                </tr>
                            )}
                        </MDBTableBody>
                    </MDBTable>
                );
            })}
        </>
    );
}

type ParseRules = {
    fields: Array<string>;
    sku: Array<string>;
    rules: Array<CodeFormatRow>;
};

function CodeFormat(props: { data: ParseRules, setData: React.Dispatch<React.SetStateAction<ParseRules>> }) {
    let { 0: __ } = useLocale();
    let doAdd = React.useCallback((idx: number, e: React.MouseEvent<HTMLButtonElement>) => {
        e.preventDefault();
        e.stopPropagation();
        props.setData(old => {
            let cloned = { ...old };
            cloned.rules[idx] = {
                min: 1,
                fields: []
            };
            return cloned;
        });
    }, [props.setData]);
    let doAddField = React.useCallback((idx: number, e: React.MouseEvent<HTMLButtonElement>) => {
        e.preventDefault();
        e.stopPropagation();
        props.setData(old => {
            let cloned = { ...old };
            cloned.fields[idx] = '';
            for (let i in cloned.rules) {
                cloned.rules[i].fields[idx] ??= '';
            }
            return cloned;
        });
    }, [props.setData]);
    let onFocus = React.useCallback((e: React.FocusEvent<HTMLTableCellElement>) => {
        let range = document.createRange();
        range.selectNodeContents(e.target);
        let selection = window.getSelection();
        selection?.removeAllRanges();
        selection?.addRange(range);
    }, []);
    let onChange = React.useCallback((row: number, key: 'priority' | 'name' | 'separator' | 'min' | 'max', e: React.FocusEvent<HTMLTableCellElement>) => {
        props.setData(old => {
            let cloned = { ...old }, value = e.target.innerText.trim();
            if (key === 'name' || key === 'separator') {
                cloned.rules[row][key] = value;
            } else if (value) {
                cloned.rules[row][key] = parseInt(value);
            } else {
                delete cloned.rules[row][key];
            }
            return cloned;
        });
    }, [props.setData]);
    let onChangeField = React.useCallback((idx: number, e: React.FocusEvent<HTMLTableCellElement>) => {
        props.setData(old => {
            let cloned = { ...old };
            cloned.fields[idx] = e.target.innerText.trim();
            return cloned;
        });
    }, [props.setData]);
    let onChangeCount = React.useCallback((row: number, col: number, e: React.FocusEvent<HTMLTableCellElement>) => {
        props.setData(old => {
            let cloned = { ...old }, value = e.target.innerText.trim();
            if (value !== '' && Number.isNaN(parseInt(value))) {
                value = '*';
            }
            cloned.rules[row].fields[col] = value;
            return cloned;
        });
    }, [props.setData]);
    let onChangeSKU = React.useCallback((e: React.FocusEvent<HTMLTableCellElement>) => {
        props.setData(old => {
            let cloned = { ...old };
            cloned.sku = e.target.innerText.trim().split(/\s*[,\uff0c]\s*/);
            return cloned;
        });
    }, [props.setData]);
    let doSave = React.useCallback(() => {
        let cloned: ParseRules = { fields: [], rules: [], sku: props.data.sku };
        for (let i in props.data.fields) {
            if (props.data.fields[i]) {
                cloned.fields.push(props.data.fields[i]);
            }
        }
        if (intersection(cloned.fields, cloned.sku).length !== cloned.sku.length) {
            Toast.show(__('All SKU fields should be specified in fields.'));
            return;
        }
        for (let row of props.data.rules) {
            if (!row.min) {
                Toast.show(__('$1 is required and cannot be empty.').replace('$1', __('Min Fields')));
                return;
            }
            if (Number.isNaN(parseInt(row.min.toString()))) {
                Toast.show(__('$1 should be numerical.').replace('$1', 'Min Fields'));
                return;
            }
            row.fields = row.fields.slice(0, cloned.fields.length);
            let stars = 0;
            for (let i in cloned.fields) {
                if (typeof row.fields[i] === 'undefined') {
                    row.fields[i] = '';
                } else if (row.fields[i] === '*') {
                    stars++;
                }
            }
            if (stars > 1) {
                Toast.show(__('Only ONE field allows to set to *.'));
                return;
            }
            if (row.max && Number.isNaN(parseInt(row.max.toString())) || !row.max) {
                row.max = undefined;
            }
            if ((!row.max || row.max > row.min) && !stars || row.max && row.max < row.min) {
                row.max = row.min;
            }
            cloned.rules.push(row);
        }
        Promise.all([
            saveParsingRules(cloned.rules),
            saveConfig({ path: 'inventory/code_format', value: JSON.stringify({ fields: cloned.fields, sku: cloned.sku }) })
        ]).then(() => {
            props.setData(cloned);
            Toast.show(__('Data has been saved.'));
        });
    }, [props.data, props.setData, __]);
    React.useEffect(() => {
        getParsingRules().then(rules => typeof rules !== 'string' && props.setData(old => ({ ...old, rules })));
    }, [props.setData]);
    return (
        <MDBTable striped hover className="inventory-settings caption-top">
            <caption>
                <button type="button" onClick={doSave}><FontAwesomeIcon icon={faSave} /></button>
                <span>{__('Code Format')}</span>
                <div className="btns">
                    <button type="button" onClick={doAddField.bind(null, props.data.fields.length)}>
                        <I18N>Add a Field</I18N>
                    </button>
                    <button type="button" onClick={doAdd.bind(null, props.data.rules.length)}>
                        <I18N>Add a Row</I18N>
                    </button>
                </div>
            </caption>
            <MDBTableHead>
                <tr>
                    <th className="w-1 text-nowrap text-center">ID</th>
                    <th className="w-1 text-nowrap text-center">{__('Priority')}</th>
                    <th>{__('Name')}</th>
                    <th className="w-1 text-nowrap text-center">{__('Separator')}</th>
                    <th className="w-1 text-nowrap text-center">{__('Min Fields')}</th>
                    <th className="w-1 text-nowrap text-center">{__('Max Fields')}</th>
                    <th className="w-1 text-nowrap text-center">{__('SKU Fields')}</th>
                    {props.data.fields.map((field, j) => (
                        <th key={field + j} className="text-nowrap text-center" contentEditable="plaintext-only" suppressContentEditableWarning onFocus={onFocus} onBlur={onChangeField.bind(null, j)}>{field}</th>
                    ))}
                </tr>
            </MDBTableHead>
            <MDBTableBody>
                {props.data.rules.map((row, i) => (
                    <tr key={i.toString()}>
                        <td className="w-1 text-nowrap text-center">{row.id}</td>
                        <td className="w-1 text-nowrap text-center" contentEditable="plaintext-only" suppressContentEditableWarning onFocus={onFocus} onBlur={onChange.bind(null, i, 'priority')}>{row.priority || '0'}</td>
                        <td contentEditable="plaintext-only" suppressContentEditableWarning onFocus={onFocus} onBlur={onChange.bind(null, i, 'name')}>{row.name}</td>
                        <td className="w-1 text-nowrap text-center" contentEditable="plaintext-only" suppressContentEditableWarning onFocus={onFocus} onBlur={onChange.bind(null, i, 'separator')}>{row.separator ?? '-'}</td>
                        <td className="w-1 text-nowrap text-center" contentEditable="plaintext-only" suppressContentEditableWarning onFocus={onFocus} onBlur={onChange.bind(null, i, 'min')}>{row.min || ''}</td>
                        <td className="w-1 text-nowrap text-center" contentEditable="plaintext-only" suppressContentEditableWarning onFocus={onFocus} onBlur={onChange.bind(null, i, 'max')}>{row.max || ''}</td>
                        {i === 0 ? (
                            <td className="w-1 text-nowrap text-center" contentEditable="plaintext-only" suppressContentEditableWarning onFocus={onFocus} onBlur={onChangeSKU} rowSpan={props.data.rules.length}>{(props.data.sku || []).join(',')}</td>
                        ) : null}
                        {props.data.fields.map((_, j) => (
                            <td key={i + 'c' + j} className="text-nowrap text-center" contentEditable="plaintext-only" suppressContentEditableWarning onFocus={onFocus} onBlur={onChangeCount.bind(null, i, j)}>{row.fields[j] || ''}</td>
                        ))}
                    </tr>
                ))}
            </MDBTableBody>
        </MDBTable>
    );
}
